import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download } from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csvExport";
import ItemSearchSelect from "@/components/ItemSearchSelect";

function Sel({ value, onChange, options, testid, placeholder }) {
  return (
    <select value={value || ""} onChange={e => onChange(e.target.value)}
      className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid={testid}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function TechIssue() {
  const [techs, setTechs] = useState([]);
  const [items, setItems] = useState([]);
  const [stock, setStock] = useState([]);
  const [tid, setTid] = useState("");
  const [iid, setIid] = useState("");
  const [qty, setQty] = useState(1);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, i, s] = await Promise.all([api.get("/technicians"), api.get("/items"), api.get("/technician-stock")]);
    setTechs(t.data.filter(x => x.status === "active" || !x.status));
    setItems(i.data); setStock(s.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!tid || !iid || !qty) return toast.error("Fill all required fields");
    setBusy(true);
    try {
      await api.post("/technician-issue", { technician_id: tid, item_id: iid, qty: parseFloat(qty), remarks });
      toast.success("Part issued to technician");
      setIid(""); setQty(1); setRemarks("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="tech-issue-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Technician Issue</h1>
        <p className="text-sm text-muted-foreground mt-1">Allocate parts from main stock to technicians.</p>
      </div>

      <Card className="p-6 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>Technician*</Label>
            <Sel value={tid} onChange={setTid} testid="tech-issue-tech" placeholder="Select technician"
              options={techs.map(t => ({ value: t.id, label: t.technician_name }))} />
          </div>
          <div>
            <Label>Item* (search by code/name/brand)</Label>
            <ItemSearchSelect
              value={iid}
              onChange={setIid}
              items={items}
              testid="tech-issue-item"
            />
          </div>
          <div>
            <Label>Quantity*</Label>
            <Input type="number" value={qty} onChange={e => setQty(e.target.value)} data-testid="tech-issue-qty" />
          </div>
          <div className="flex items-end">
            <Button onClick={submit} disabled={busy} className="w-full bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white" data-testid="tech-issue-submit">
              {busy ? "Issuing..." : "Issue Part"}
            </Button>
          </div>
        </div>
        <div className="mt-4">
          <Label>Remarks</Label>
          <Input value={remarks} onChange={e => setRemarks(e.target.value)} data-testid="tech-issue-remarks" />
        </div>
      </Card>

      <Card className="p-0 border border-border overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="font-display font-semibold">Current Technician Stock</div>
          <Button variant="outline" size="sm" onClick={() => exportToCSV(stock, [
            { label: "Technician", key: "technician_name" },
            { label: "Item Code", key: "item_code" },
            { label: "Item Name", key: "item_name" },
            { label: "Brand", key: "brand" },
            { label: "Qty Held", key: "qty" },
          ], "technician_stock")} data-testid="tech-stock-export">
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Technician</TableHead>
              <TableHead>Item Code</TableHead>
              <TableHead>Item Name</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead className="text-right">Qty Held</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stock.map(s => (
              <TableRow key={s.id}>
                <TableCell>{s.technician_name}</TableCell>
                <TableCell className="font-mono text-xs">{s.item_code}</TableCell>
                <TableCell>{s.item_name}</TableCell>
                <TableCell>{s.brand}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{s.qty}</TableCell>
              </TableRow>
            ))}
            {!stock.length && <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No technician holds any stock currently.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export function TechReturn() {
  const [techs, setTechs] = useState([]);
  const [stock, setStock] = useState([]);
  const [tid, setTid] = useState("");
  const [iid, setIid] = useState("");
  const [qty, setQty] = useState(1);
  const [rtype, setRtype] = useState("FRESH");
  const [callId, setCallId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, s] = await Promise.all([api.get("/technicians"), api.get("/technician-stock")]);
    setTechs(t.data); setStock(s.data);
  };
  useEffect(() => { load(); }, []);

  const techStock = stock.filter(s => s.technician_id === tid);

  const submit = async () => {
    if (!tid || !iid || !qty) return toast.error("Fill required fields");
    if (rtype === "CONSUMED" && !callId.trim()) return toast.error("Call ID required for Consumed items");
    setBusy(true);
    try {
      await api.post("/technician-return", { technician_id: tid, item_id: iid, qty: parseFloat(qty), return_type: rtype, call_id: callId, remarks });
      toast.success(rtype === "FRESH" ? "Fresh part returned to stock" : "Part marked as consumed");
      setIid(""); setQty(1); setRemarks(""); setCallId("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="tech-return-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Technician Return / Consume</h1>
        <p className="text-sm text-muted-foreground mt-1">Record fresh part returns (back to stock) or consumed usage.</p>
      </div>

      <Card className="p-6 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <Label>Technician*</Label>
            <Sel value={tid} onChange={v => { setTid(v); setIid(""); }} testid="tech-return-tech" placeholder="Select technician"
              options={techs.map(t => ({ value: t.id, label: t.technician_name }))} />
          </div>
          <div>
            <Label>Item* (from technician's stock)</Label>
            <ItemSearchSelect
              value={iid}
              onChange={setIid}
              items={techStock.map(s => ({ id: s.item_id, item_code: s.item_code, item_name: s.item_name, brand: s.brand, current_stock: s.qty }))}
              testid="tech-return-item"
              placeholder="Search technician's held items..."
            />
          </div>
          <div>
            <Label>Qty*</Label>
            <Input type="number" value={qty} onChange={e => setQty(e.target.value)} data-testid="tech-return-qty" />
          </div>
          <div>
            <Label>Type*</Label>
            <Sel value={rtype} onChange={setRtype} testid="tech-return-type" placeholder=""
              options={[{ value: "FRESH", label: "Fresh (Return to Stock)" }, { value: "CONSUMED", label: "Consumed (Used up)" }]} />
          </div>
          <div className="flex items-end">
            <Button onClick={submit} disabled={busy} className="w-full bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white" data-testid="tech-return-submit">
              {busy ? "Saving..." : "Submit"}
            </Button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {rtype === "CONSUMED" && (
            <div>
              <Label>Call ID / Service Ticket* <span className="text-xs text-muted-foreground">(mandatory for consumed)</span></Label>
              <Input value={callId} onChange={e => setCallId(e.target.value)} data-testid="tech-return-callid" placeholder="e.g. CALL-1234, HW-INV-9876" />
            </div>
          )}
          <div className={rtype === "CONSUMED" ? "" : "md:col-span-2"}>
            <Label>Remarks</Label>
            <Input value={remarks} onChange={e => setRemarks(e.target.value)} data-testid="tech-return-remarks" />
          </div>
        </div>
      </Card>
    </div>
  );
}
